import { useMemo } from 'react'
import { Button } from '@chakra-ui/react'
import { SkipNavContent, SkipNavLink } from '@chakra-ui/skip-nav'
import Head from 'next/head'
import classNames from 'classnames'
import Footer from '~/components/shared/Footer/Footer'
import { Header } from '~/components/shared/Header'
import SpinnerOverlay from '~/components/shared/SpinnerOverlay'
import { usePlatformTheme } from '~/context/theme'
import { usePartner } from '~/context/partner'
import { useUser } from '~/context/user'
import { useAuthContext } from '~/context/auth'
import { TagManagerProvider } from '~/context/analytics'
import { useAccount } from '~/context/account'
import { StickyBanner } from '@reward-platform/lift/components'
import { useBasketItemsCount } from '~/hooks/useBasket/useBasket'
import { getSharedSpacesEnvironment } from '~/utils/getSharedSpacesEnvironment'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import { NotificationBar } from '~/context/notification'
import { LayoutProps } from './Layout.types'
import ChatWidgetMock from '../ChatWidgetMock'
import BARCMemberLayout from './BARCMemberLayout'
import { StickyBanners } from '../StickyBanners'

import styles from './Layout.module.scss'

const SKIP_TO_CONTENT_ID = 'skip-to-content'
const SKIP_TO_FOOTER_ID = 'skip-to-footer'
const sharedSpacesAccessToken = process.env.NEXT_PUBLIC_SHARED_SPACES_CMS_ACCESS_TOKEN ?? ''

const SharedComponentsProvider = dynamic(
  () => import('@channel/shared-header-footer').then((module) => module.SharedComponentsProvider),
  { ssr: false }
)

type BaseLayoutProps = LayoutProps & {
  isLoading?: boolean
}

const BaseLayout = ({
  children,
  title,
  sparse = false,
  variant = 'base',
  isLoading,
}: BaseLayoutProps): JSX.Element => {
  const partner = usePartner()
  const { isAviosTheme } = usePlatformTheme()

  const { aviosBalance, householdAviosBalance, isError: isAccountError } = useAccount()
  const { isAuthenticated, user, userId } = useAuthContext()
  const basketItemsCount = useBasketItemsCount()

  const userDisplayName = useMemo(() => user?.name ?? '', [user])
  const membershipNumber = useMemo(() => userId ?? '', [userId])

  const t = useTranslations('components.layout.actions')

  const pageTitle = title ? `${partner.name} - ${title}` : `Avios - ${partner.name}`

  return (
    <TagManagerProvider>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Turn your everyday spending into exciting travel rewards - discover more &amp; shop with Avios."
        />
      </Head>
      <StickyBanner.StickyContainer>
        <NotificationBar />
      </StickyBanner.StickyContainer>
      <div className={classNames(styles.wrapper, { [variant]: true })}>
        {!isAviosTheme ? (
          <>
            {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <SkipNavLink id={SKIP_TO_CONTENT_ID} className={styles.skipLink}>
                <Button variant="link" tabIndex={-1}>
                  {t('skip_to_main_content')}
                </Button>
              </SkipNavLink>
            }
            {!sparse ? (
              <SkipNavLink id={SKIP_TO_FOOTER_ID} className={styles.skipLink}>
                <Button variant="link" tabIndex={-1}>
                  {t('skip_to_footer')}
                </Button>
              </SkipNavLink>
            ) : null}
          </>
        ) : null}
        {sparse || (
          <Header
            userDisplayName={userDisplayName}
            membershipNumber={membershipNumber}
            aviosBalance={aviosBalance ?? 0}
            householdAviosBalance={householdAviosBalance}
            aviosBalanceError={isAccountError}
            basketItemsCount={basketItemsCount}
            isLoading={isLoading}
            skipContentSelector={SKIP_TO_CONTENT_ID}
          />
        )}
        <StickyBanner.StickyContainer>
          <StickyBanners />
        </StickyBanner.StickyContainer>
        <main id="content" className={styles.content}>
          <SkipNavContent id={SKIP_TO_CONTENT_ID} />
          {isLoading || (user === undefined && isAuthenticated) ? <SpinnerOverlay /> : children}
        </main>
        {!isAuthenticated ? <ChatWidgetMock /> : null}
        {sparse || (
          <>
            <SkipNavContent id={SKIP_TO_FOOTER_ID} />
            <Footer />
          </>
        )}
      </div>
    </TagManagerProvider>
  )
}

const Layout = (props: LayoutProps) => {
  const { user } = useUser()
  const environment = useMemo(() => getSharedSpacesEnvironment(), [])
  const { isAviosTheme } = usePlatformTheme()
  const partner = usePartner()

  const isBARCmember =
    partner.code === 'BA' && user?.memberIdentifier?.identifier?.startsWith('OWVE')

  if (isBARCmember) {
    return <BARCMemberLayout />
  }

  if (isAviosTheme) {
    return (
      <SharedComponentsProvider
        cms={{
          accessToken: sharedSpacesAccessToken,
          environment,
        }}
      >
        <BaseLayout {...props} isLoading={false} />
      </SharedComponentsProvider>
    )
  }

  return <BaseLayout {...props} isLoading={false} />
}

export const AuthenticatedEmptyPageLayout = ({ children }: LayoutProps) => {
  const { user, isLoading: isUserLoading } = useUser()
  const userIsLoaded = user && !isUserLoading

  return userIsLoaded ? (
    <TagManagerProvider>
      <div className={styles.centerContent}>{children}</div>
    </TagManagerProvider>
  ) : null
}

export default Layout
